import { create } from "zustand";
import apiClient from "@services/api/apiClient";
import API, { apiWithStatus, STATUS } from "@config/shared/api-paths";

/**
 * Enabled feature tree (status PUBLISHED). Single source of truth for BOTH the
 * admin nav MENU and the Features settings panel's checkboxes — deliberately
 * separate from AuthContext.features (which also drives route generation), so a
 * toggle re-renders only these two subtrees, not the page or the routes.
 *
 * Two hard requirements shape this store:
 *  1. Stay UP TO DATE with the backend. A toggle can re-parent children to root,
 *     and other admins can change features out-of-band — so after a mutation we
 *     RECONCILE against the backend rather than trusting a local derivation.
 *  2. Stay in SYNC across tabs. A toggle in one tab BroadcastChannel-sends its
 *     ALREADY-SETTLED sections to the others, which apply them directly. It does
 *     NOT tell them to refetch: status/4 is eventually consistent, so a sibling's
 *     own fetch could catch a transient drop (the "vanishing features" bug) with
 *     no self-correction — and a refetch would fire an admin GET from tabs that
 *     may be logged out or on the client portal.
 *
 * The catch: status/4 is eventually consistent — right after a PUT it can
 * transiently drop UNRELATED features. So `reconcile` polls until the backend's
 * enabled-id set matches the intended set before committing it; if it never
 * converges we keep the optimistic tree rather than commit a half-baked one.
 */
const MENU_URL = apiWithStatus(API.BACKEND.FEATURES, STATUS.PUBLISHED);
const SYNC_CHANNEL = "oxygen:feature-menu";
const RECONCILE_TRIES = 6;
const RECONCILE_DELAY_MS = 400;

const channel =
  typeof BroadcastChannel !== "undefined" ? new BroadcastChannel(SYNC_CHANNEL) : null;

// Every node id in a tree, at ANY depth — the nav renders arbitrary depth
// (side-nav-section recurses), so a 2-level-only walk would make reconcile's
// enabled-id comparison wrong the moment the backend tree grows past 2 levels.
const collectIds = (roots) => {
  const ids = new Set();
  const walk = (nodes) =>
    (nodes || []).forEach((n) => {
      ids.add(n.node.id);
      walk(n.children);
    });
  walk(roots);
  return ids;
};

const sameIds = (ids, expected) =>
  ids.size === expected.length && expected.every((id) => ids.has(id));

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const fetchMenu = async () => {
  const { data } = await apiClient.get(MENU_URL);
  return data?.roots ?? [];
};

// Monotonic guard so a slow reconcile/load from an earlier toggle can never
// clobber the state written by a newer one (rapid toggling, or a cross-tab
// reload landing mid-reconcile).
let syncGeneration = 0;

const useFeatureMenuStore = create((set, get) => ({
  sections: [],
  loaded: false,

  // Fetch the enabled tree from the backend (source of truth) and commit it.
  load: async () => {
    const gen = ++syncGeneration;
    try {
      const sections = await fetchMenu();
      if (gen === syncGeneration) set({ sections, loaded: true });
    } catch (error) {
      console.error(error);
      if (gen === syncGeneration) set({ loaded: true });
    }
    return get().sections;
  },

  // Show a locally-derived tree immediately (optimistic, acting tab only). Bumps
  // the generation so an in-flight reconcile from a prior toggle can't overwrite
  // this fresher state.
  setSections: (sections) => {
    syncGeneration += 1;
    set({ sections, loaded: true });
  },

  // Converge the store to the backend's real enabled tree after a toggle. Polls
  // status/4 until its enabled-id set equals `expected` (the ids we intend to be
  // enabled), then commits that authoritative tree. Guarded by generation so a
  // newer toggle wins. Returns true once committed, false if it never converged.
  reconcile: async (expected) => {
    const gen = ++syncGeneration;
    for (let attempt = 0; attempt < RECONCILE_TRIES; attempt += 1) {
      try {
        const sections = await fetchMenu();
        if (gen !== syncGeneration) return false; // superseded by a newer change
        if (sameIds(collectIds(sections), expected)) {
          set({ sections, loaded: true });
          return true;
        }
      } catch (error) {
        console.error(error);
        if (gen !== syncGeneration) return false;
      }
      await wait(RECONCILE_DELAY_MS);
      if (gen !== syncGeneration) return false;
    }
    console.warn("Feature menu did not converge with the backend; kept optimistic tree.");
    return false;
  },

  // Send the SETTLED sections to every OTHER tab (see requirement 2 above) so
  // they apply them directly, without a refetch.
  broadcastReload: (sections) => channel?.postMessage({ type: "sections", sections }),
}));

// Another tab toggled → apply the settled sections it sent. No refetch (avoids
// the transient-drop bug and a spurious admin GET from non-admin tabs).
if (channel) {
  channel.onmessage = (event) => {
    const data = event?.data;
    if (data && data.type === "sections" && Array.isArray(data.sections)) {
      useFeatureMenuStore.getState().setSections(data.sections);
    }
  };
}

export default useFeatureMenuStore;
